import React, {
  FunctionComponent,
  useEffect,
  useState,
  useContext,
} from "react";
import URI from "urijs";
import { useFlags } from "launchdarkly-react-client-sdk";
import { AppStore } from "../../app/store";
import {
  ActionType,
  Plan,
  SearchResultPlan,
  MctEvent,
  PlanTypeLabels,
} from "../../@types";
import {
  CONSTANTS as C,
  logError,
  parsePlanLongIdIntoUrlPartForApi,
  useFormatMessage,
  useTranslate,
} from "../../helpers";
import { AppPage, PrintButton, EnrollmentDownAlert } from "../../components";
import { PlanDetailsErrorPage } from "../PlanDetailsPage";

import PlanCompareHeadings from "./PlanCompareHeadings";
import GoBack from "./GoBack";
import PlanComparisonPlanCard from "./PlanComparisonPlanCard";
import { withRouter, RouteComponentProps } from "react-router";
import { getPlan } from "../../api";
import { isCurrentPlan } from "../../helpers/beneficiaryInfoHelpers";
import { FormattedHTMLMessage } from "react-intl-phraseapp";
import swipeSVG from "../../assets/swipe.svg";
import { isWindowMobileWidth } from "../../helpers/objectUtilities";
import routes from "../../app/routes";

const validatePlanIds = (idList: string | (string | null)[] | null): void => {
  if (!idList) {
    throw new Error("Plans list variable was not provided in query");
  }

  // the URI api returns a string value when a single "plans" variable is provided
  if (typeof idList === "string") {
    throw new Error("Only one plan ID was provided");
  }

  if (idList.length < C.compareFooter.minPlansToCompare) {
    throw new Error(
      `The query string to the plan compare path requires a minimum of ${C.compareFooter.minPlansToCompare} IDs`
    );
  }

  // ensure that each longId can be split into fours
  if (!idList.every(longId => longId?.split("-").length === 4)) {
    throw new Error(
      `One or more plan ID values could not be split into four segments`
    );
  }
};

const PlanComparisonPage: FunctionComponent<RouteComponentProps> = props => {
  const [plans, updatePlans] = useState<Plan[]>([]);
  const [pageError, setPageError] = useState(false);
  const {
    state: {
      year,
      compareList,
      currentCoverage,
      lis,
      futureLis,
      nextYearCoverage,
    },
    dispatch,
  } = useContext(AppStore.AppContext);
  const FormattedMessage = useFormatMessage();
  const t = useTranslate();
  const { mctCurrentPlanYear = "" } = useFlags();
  const [plansLoading, setPlansLoading] = useState(true);

  const getIdsFromQuery = (): string[] => {
    const { location } = props;
    const hasQueryString = location && location.search;
    const locationURI = new URI(location.search);

    // true, if the query string contains atleast one plans variable
    const hasPlansList = locationURI.hasQuery("plans");

    let planIdList = null;
    if (hasQueryString && hasPlansList) {
      planIdList = locationURI.search(true).plans;
    } else {
      throw new Error("Plans list variable was not provided in query");
    }

    validatePlanIds(planIdList);
    return planIdList as string[];
  };

  useEffect(() => {
    let planIdList: string[] = [];

    try {
      planIdList = getIdsFromQuery();
    } catch (e) {
      console.error(
        "The following exception occured while validating query parameters to the plan compare page: ",
        e.message
      );
      setPageError(true);
    }

    const lisToSend =
      year !== mctCurrentPlanYear.toString() && futureLis ? futureLis : lis;

    Promise.all(
      (planIdList as string[]).map(id => {
        const apiURL = parsePlanLongIdIntoUrlPartForApi(id);
        return getPlan(apiURL, lisToSend ? { lis: lisToSend } : {});
      })
    )
      .then(plans =>
        plans.reduce((acc, plan) => {
          if (plan) acc.push(plan);
          return acc;
        }, [] as Plan[])
      )
      .then(planData => {
        setPlansLoading(false);
        updatePlans(planData);
      })
      .catch(error => {
        setPageError(true);
        logError("Failed to retrieve plan (PlanComparisonPage)", error);
      });
  }, []);

  const makeRowsEqualHeightAndAdjustWidths = (): void => {
    const isMobileWidth = isWindowMobileWidth(window);

    /** Gets all of the cells from each column that share a row and sets them to the same height */
    const columns = document.getElementsByClassName("planCompare__col");

    if (columns.length < 2) {
      return;
    }

    const cellsByColumn = Array.from(columns).map(col =>
      Array.from(col.getElementsByClassName("planCompare__cell"))
    );

    const numberOfCellsInEachColumn = cellsByColumn[0].length;

    for (let i = 0; i < numberOfCellsInEachColumn; i++) {
      // Reset cell heights
      cellsByColumn.forEach(col => {
        if (col[i]) {
          (col[i] as HTMLElement).style.height = "auto";
        }
      });

      // Get varying cell heights
      const cellHeights = cellsByColumn.map(
        col => (col[i] && col[i].clientHeight) || 0
      );

      // Find the tallest cell
      const newHeight = Math.max(...cellHeights);

      // Apply the tallest height to all other cells in row
      cellsByColumn.forEach(col => {
        if (col[i]) {
          const h = isMobileWidth ? "auto" : `${newHeight}px`;
          (col[i] as HTMLElement).style.height = h;
        }
      });
    }

    // Adjust the .columnSection widths since IE has trouble with flex
    const columnSections = document.querySelectorAll(".columnSection");
    Array.from(columnSections).forEach(section => {
      if (section) {
        if (isMobileWidth) {
          let sectionWidth = Array.from(
            section.querySelectorAll(".planCompare__cell")
          ).reduce((acc, cell) => {
            acc += cell.clientWidth;
            return acc;
          }, 0);
          if (section.classList.contains("columnSection__wrap")) {
            sectionWidth = sectionWidth / 1.75;
          }
          (section as HTMLElement).style.minWidth = `${sectionWidth}px`;
        } else {
          (section as HTMLElement).style.minWidth = "auto";
        }
      }
    });
  };

  useEffect(() => {
    if (!plans.length && !plansLoading) {
      props.history.push(routes.searchResults);
    } else {
      makeRowsEqualHeightAndAdjustWidths();
    }
  }, [plans]);

  window.addEventListener(
    MctEvent.costsLoaded,
    makeRowsEqualHeightAndAdjustWidths
  );

  let recentlyResized = false;

  window.addEventListener("resize", () => {
    if (!recentlyResized) {
      window.setTimeout(() => {
        recentlyResized = false;
        makeRowsEqualHeightAndAdjustWidths();
      }, 500);
    }
    recentlyResized = true;
  });

  const onPlanRemove = (planToRemove: Plan): void => {
    const removePlanFilter = (plan: Plan | SearchResultPlan): boolean =>
      plan.id !== planToRemove.id;

    updatePlans(plans.filter(removePlanFilter));

    dispatch({
      type: ActionType.UPDATE_COMPARE_LIST,
      payload: compareList.filter(removePlanFilter),
    });
  };

  return !pageError ? (
    <AppPage
      heading={
        plans[0] &&
        FormattedMessage(
          { id: "plan_compare_page.comparing_plans" },
          {
            count: plans.length,
            planTypeTranslation: t(PlanTypeLabels[plans[0].plan_type]),
          }
        )
      }
      headingClass="mct-u-mobile-only e2e-comparing-plans"
      subHeading={
        <>
          {plans[0] && (
            <EnrollmentDownAlert
              plan={plans[0]}
              className="ds-u-margin-bottom--3"
            />
          )}
          <div className="ds-l-row ds-u-justify-content--end">
            <PrintButton
              tealiumProps={{
                event_action: "Find Plans - Compare Plans",
                event_label: "Print",
              }}
            />
          </div>
        </>
      }
    >
      <div className="planCompare">
        <div className="planCompare__col planCompare__headingCol">
          {plans[0] && (
            <PlanCompareHeadings count={plans.length} plan={plans[0]} />
          )}
        </div>

        {/* FIXME: Replace with 'official' ScrollTable component */}
        <div className="ScrollTable__MobileHelp" aria-hidden="true">
          <img aria-hidden="true" src={swipeSVG} role="presentation" alt=" " />
          <span>
            <FormattedHTMLMessage id="scroll_table.swipe_to_view_more" />
          </span>
        </div>
        {plans.map(plan => (
          <PlanComparisonPlanCard
            key={plan.id}
            plan={plan}
            onPlanRemove={onPlanRemove}
            isCurrentPlan={isCurrentPlan(currentCoverage, plan)}
            isNextYearOfCurrentPlan={isCurrentPlan(nextYearCoverage, plan)}
          />
        ))}
      </div>
      <GoBack mobileOnly />
    </AppPage>
  ) : (
    <PlanDetailsErrorPage />
  );
};

export default withRouter(PlanComparisonPage);
